import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Bell, ArrowsClockwise } from '@phosphor-icons/react';
import { formatLabel, STATUS_STYLES, HIT_STATUS_STYLES, BADGE, timeAgo } from '@/lib/format';
import ResolutionModal from '@/components/ResolutionModal';
import BulkActionBar from '@/components/BulkActionBar';

export default function MonitoringPage() {
  const [tab, setTab] = useState('hits');
  const [stats, setStats] = useState(null);
  const [hits, setHits] = useState([]);
  const [hitsTotal, setHitsTotal] = useState(0);
  const [cases, setCases] = useState([]);
  const [runs, setRuns] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [triggering, setTriggering] = useState(false);
  const [resModal, setResModal] = useState({ open: false, id: null, resolution: null, matchName: null });
  // Bulk selection — only pending hits are selectable
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();
  useEffect(() => { document.title = "Radar — Monitoring"; }, []);

  const fetchData = useCallback(async () => {
    try {
      const [s, h, c, r] = await Promise.all([
        api.get('/monitoring/stats'),
        api.get('/monitoring/hits', { params: { status: statusFilter || undefined } }),
        api.get('/monitoring/cases'),
        api.get('/monitoring/runs'),
      ]);
      setStats(s.data);
      setHits(h.data.hits);
      setHitsTotal(h.data.total);
      setCases(c.data.cases);
      setRuns(r.data.runs);
    } catch (e) { }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTrigger = async () => {
    setTriggering(true);
    try { await api.post('/monitoring/run'); alert('Monitoring cycle triggered.'); setTimeout(fetchData, 3000); } catch (e) { alert('Failed'); }
    setTriggering(false);
  };

  const handleResolve = async (hitId, status) => {
    const hit = hits.find(h => h.id === hitId);
    setResModal({ open: true, id: hitId, resolution: status, matchName: hit?.matched_entry_name });
  };

  const confirmMonitoringResolution = async (comment) => {
    try {
      await api.put(`/monitoring/hits/${resModal.id}/resolve`, { status: resModal.resolution, comment });
      setResModal({ open: false, id: null, resolution: null, matchName: null });
      fetchData();
    } catch (e) { alert('Failed'); }
  };

  const handleBulkDismiss = async (hitsToResolve) => {
    if (!hitsToResolve.length) return;
    if (!window.confirm(`Dismiss ${hitsToResolve.length} hits below 50%?`)) return;
    for (const h of hitsToResolve) {
      try { await api.put(`/monitoring/hits/${h.id}/resolve`, { status: 'dismissed', comment: 'Bulk dismissed (below threshold)' }); } catch {}
    }
    fetchData();
  };

  // ─── Multi-select bulk actions ─────────────────────────────────
  const pendingHits = hits.filter(h => h.status === 'pending');
  const allPendingSelected = pendingHits.length > 0 && pendingHits.every(h => selectedIds.has(h.id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllPending = () => {
    setSelectedIds(prev => {
      if (allPendingSelected) return new Set();
      return new Set(pendingHits.map(h => h.id));
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Bulk action — reuses the EXACT same single-hit endpoint, just loops it.
  // Uses Promise.allSettled so one failure never aborts the batch.
  const runBulkAction = async (status, comment) => {
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(
      ids.map(id => api.put(`/monitoring/hits/${id}/resolve`, { status, comment: comment || `Bulk ${status}` }))
    );
    let ok = 0, failed = 0;
    const failedIds = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') ok += 1;
      else { failed += 1; failedIds.push(ids[i]); }
    });
    if (failed > 0) {
      console.warn('[Monitoring bulk action] failures:', failedIds, results.filter(r => r.status === 'rejected'));
    }
    // Refresh and drop the succeeded ones from selection; keep failed ones selected for retry.
    await fetchData();
    setSelectedIds(new Set(failedIds));
    return { ok, failed };
  };

  // 2B: Ageing indicator (hours since detection)
  const getAgeLabel = (detectedAt) => {
    if (!detectedAt) return '';
    const hrs = (Date.now() - new Date(detectedAt).getTime()) / 3600000;
    if (hrs > 48) return 'OVERDUE';
    if (hrs > 24) return `${Math.floor(hrs)}h`;
    return '';
  };

  const renderHitCard = (h, isCritical) => {
    const selectable = h.status === 'pending';
    const checked = selectedIds.has(h.id);
    return (
      <div key={h.id} className={`bg-white border rounded-md p-4 ${isCritical ? 'border-red-300 bg-red-50/30' : h.status === 'pending' ? 'border-red-200' : 'border-[#E4E4E7]'} ${checked ? 'ring-2 ring-[#0052FF]/40' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            {selectable && (
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleSelect(h.id)}
                className="mt-1 w-4 h-4 cursor-pointer accent-[#0052FF]"
                data-testid={`hit-checkbox-${h.id}`}
                aria-label={`Select hit ${h.matched_entry_name}`}
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium">{h.matched_entry_name}</p>
                <span className="text-xs font-bold" style={{ color: h.match_score >= 80 ? '#FF3B30' : h.match_score >= 60 ? '#FF9500' : '#FFCC00' }}>{h.match_score?.toFixed(1)}%</span>
                {h.status === 'pending' && getAgeLabel(h.detected_at) && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getAgeLabel(h.detected_at) === 'OVERDUE' ? 'bg-red-100 text-red-800 animate-pulse' : 'bg-yellow-100 text-yellow-800'}`}>{getAgeLabel(h.detected_at)}</span>
                )}
              </div>
              <div className="flex gap-3 mt-1 text-xs text-[#52525B]">
                <span className="uppercase font-medium">{h.hit_type}</span>
                {h.matched_entry_source && <span>{h.matched_entry_source}</span>}
              </div>
              <button onClick={() => navigate(`/cases/${h.case_id}`)} className="text-xs text-[#0052FF] hover:underline mt-1">Case: {h.case_name}</button>
              {h.source_url && (
                <a
                  href={h.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hit-article-link-${h.id}`}
                  className="text-xs text-[#0052FF] hover:underline mt-1 ml-3"
                  onClick={e => e.stopPropagation()}
                >
                  Article link ↗
                </a>
              )}
              <p className="text-[10px] text-[#A1A1AA] mt-0.5">{new Date(h.detected_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={HIT_STATUS_STYLES[h.status] || ''}>{formatLabel(h.status)}</span>
            {h.status === 'pending' && (
              <div className="flex gap-1">
                <button onClick={() => handleResolve(h.id, 'cleared')} className="px-2 py-0.5 text-[10px] bg-green-50 text-green-700 rounded hover:bg-green-100">Clear</button>
                <button onClick={() => handleResolve(h.id, 'escalated')} className="px-2 py-0.5 text-[10px] bg-orange-50 text-orange-700 rounded hover:bg-orange-100">Escalate</button>
                <button onClick={() => handleResolve(h.id, 'dismissed')} className="px-2 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Dismiss</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="monitoring-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">Monitoring</h1>
          <p className="text-sm text-[#52525B]">Ongoing monitoring of cases against live data sources</p>
        </div>
        <button onClick={handleTrigger} disabled={triggering} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">
          <ArrowsClockwise size={16} className={triggering ? 'animate-spin' : ''} /> {triggering ? 'Running...' : 'Run Monitoring Now'}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Monitored Cases', value: stats.monitored_cases, color: '#0052FF' },
            { label: 'Pending Hits', value: stats.pending_hits, color: '#FF3B30' },
            { label: 'Cleared', value: stats.cleared_hits, color: '#34C759' },
            { label: 'Escalated', value: stats.escalated_hits, color: '#FF9500' },
            { label: 'Dismissed', value: stats.dismissed_hits, color: '#A1A1AA' },
            { label: 'Total Hits', value: stats.total_hits, color: '#52525B' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-[#E4E4E7] rounded-md p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#52525B]">{s.label}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E4E4E7]">
        {[{ id: 'hits', label: 'Alerts' }, { id: 'settings', label: 'Settings & History' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${tab === t.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>{t.label}</button>
        ))}
      </div>

      {/* HITS TAB — 2B: Prioritised with critical section, ageing, bulk actions */}
      {tab === 'hits' && (
        <div>
          <div className="flex gap-2 mb-3">
            {['', 'pending', 'cleared', 'escalated', 'dismissed'].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); clearSelection(); }} className={`px-3 py-1 text-xs rounded font-medium transition-all ${statusFilter === s ? 'bg-[#0052FF] text-white' : 'bg-white border border-[#E4E4E7] text-[#52525B] hover:bg-[#F0F0F1]'}`}>{s || 'All'}</button>
            ))}
          </div>

          {/* Select-all row (only when pending hits exist) */}
          {pendingHits.length > 0 && (
            <div className="flex items-center gap-2 mb-2 text-xs text-[#52525B]" data-testid="bulk-select-all-row">
              <input
                type="checkbox"
                checked={allPendingSelected}
                onChange={toggleSelectAllPending}
                className="w-4 h-4 cursor-pointer accent-[#0052FF]"
                data-testid="hit-select-all"
                aria-label="Select all pending hits"
              />
              <label className="cursor-pointer" onClick={toggleSelectAllPending}>
                {allPendingSelected ? 'Deselect' : 'Select'} all {pendingHits.length} pending hit{pendingHits.length === 1 ? '' : 's'}
              </label>
            </div>
          )}

          {/* 2B: Critical Hits Section (score > 80) */}
          {(() => {
            const critical = hits.filter(h => h.match_score >= 80 && h.status === 'pending');
            if (critical.length === 0) return null;
            return (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                  <h4 className="text-xs uppercase tracking-wider font-bold text-red-700">Critical Hits ({critical.length}) — Score &gt; 80%</h4>
                </div>
                <div className="space-y-2">
                  {critical.map(h => renderHitCard(h, true))}
                </div>
              </div>
            );
          })()}

          {/* 2B: Grouped by case */}
          {(() => {
            const grouped = {};
            const nonCritical = hits.filter(h => !(h.match_score >= 80 && h.status === 'pending'));
            nonCritical.forEach(h => {
              const key = h.case_name || h.case_id || 'Unknown';
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(h);
            });
            return Object.entries(grouped).map(([caseName, caseHits]) => (
              <div key={caseName} className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-[#0A0A0A]">{caseName} <span className="text-[#A1A1AA] font-normal">({caseHits.length} hits)</span></h4>
                  {/* 2B: Bulk dismiss */}
                  {caseHits.some(h => h.status === 'pending') && (
                    <button onClick={() => handleBulkDismiss(caseHits.filter(h => h.status === 'pending' && h.match_score < 50))} className="text-[10px] px-2 py-0.5 border border-[#E4E4E7] rounded text-[#52525B] hover:bg-[#F0F0F1]">
                      Dismiss all &lt;50% for this case
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {caseHits.map(h => renderHitCard(h, false))}
                </div>
              </div>
            ));
          })()}

          {hits.length === 0 && (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-8 text-center text-sm text-[#A1A1AA]">No monitoring hits found.</div>
          )}
        </div>
      )}

      {/* SETTINGS & HISTORY TAB (merged cases + runs) */}
      {tab === 'settings' && (
        <div>
          <h3 className="font-heading text-base font-semibold mb-3">Monitored Cases</h3>
          <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden mb-6">
          <table className="w-full">
            <thead><tr className="border-b border-[#E4E4E7]">
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Name</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Risk</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Pending Hits</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Total Hits</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Last Monitored</th>
            </tr></thead>
            <tbody>
              {cases.map(c => (
                <tr key={c.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8] cursor-pointer" onClick={() => navigate(`/cases/${c.id}`)}>
                  <td className="px-4 py-3 text-sm font-medium">{c.full_name}</td>
                  <td className="px-4 py-3"><span className={STATUS_STYLES[c.status] || ''}>{formatLabel(c.status)}</span></td>
                  <td className="px-4 py-3 text-xs font-medium">{c.risk_rating}</td>
                  <td className="px-4 py-3"><span className={`text-sm font-bold ${c.pending_hits > 0 ? 'text-red-600' : 'text-green-600'}`}>{c.pending_hits}</span></td>
                  <td className="px-4 py-3 text-sm">{c.total_hits}</td>
                  <td className="px-4 py-3 text-xs text-[#A1A1AA]">{c.last_monitored_at ? timeAgo(c.last_monitored_at) : 'Never'}</td>
                </tr>
              ))}
              {cases.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#A1A1AA]">No monitored cases. Enable monitoring on individual cases.</td></tr>}
            </tbody>
          </table>
          </div>

          <h3 className="font-heading text-base font-semibold mb-3">Sync & Run History</h3>
          <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-[#E4E4E7]">
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Run ID</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Type</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Completed</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Cases Screened</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">New Hits</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
            </tr></thead>
            <tbody>
              {runs.map((r, i) => (
                <tr key={i} className="border-b border-[#E4E4E7]">
                  <td className="px-4 py-3 text-xs font-mono text-[#52525B]">{r.run_id?.slice(0, 8)}...</td>
                  <td className="px-4 py-3 text-xs">{formatLabel(r.run_type)}</td>
                  <td className="px-4 py-3 text-xs">{r.completed_at ? timeAgo(r.completed_at) : '-'}</td>
                  <td className="px-4 py-3 text-sm font-medium">{r.cases_screened}</td>
                  <td className="px-4 py-3"><span className={`text-sm font-bold ${r.new_hits_found > 0 ? 'text-red-600' : 'text-green-600'}`}>{r.new_hits_found}</span></td>
                  <td className="px-4 py-3"><span className={`${BADGE} bg-green-50 text-green-700 border-green-200`}>{formatLabel(r.status)}</span></td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#A1A1AA]">No monitoring runs yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Resolution Modal */}
      <ResolutionModal isOpen={resModal.open} resolution={resModal.resolution} matchName={resModal.matchName} onConfirm={confirmMonitoringResolution} onCancel={() => setResModal({ open: false, id: null, resolution: null, matchName: null })} />

      {/* Bulk Action Bar — shown when >=1 pending hits selected */}
      <BulkActionBar
        selectedCount={selectedIds.size}
        onAction={(actionKey, comment) => runBulkAction(actionKey, comment)}
        onClear={clearSelection}
        actions={[
          { key: 'cleared',   label: 'Clear',    className: 'bg-green-50 text-green-700 hover:bg-green-100' },
          { key: 'escalated', label: 'Escalate', className: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
          { key: 'dismissed', label: 'Dismiss',  className: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
        ]}
      />
    </div>
  );
}
